import { PageHeader } from "@/components/PageHeader";
import * as shared from "@/styles/shared";
import { useMeterHistory, useMeterProviders } from "@shared/hooks/meter";
import { buildMeterProviderTabItems } from "@shared/utils";
import { Camera } from "lucide-react";
import { useTranslation } from "react-i18next";
import CaptureCard from "./meterReadings/CaptureCard";
import HistoryCard from "./meterReadings/HistoryCard";
import NotEnabledCard from "./meterReadings/NotEnabledCard";
import ProviderTabs from "./meterReadings/ProviderTabs";
import SettingsCard from "./meterReadings/SettingsCard";
import { useMeterCapture } from "./meterReadings/useMeterCapture";
import { useMeterSettingsForm } from "./meterReadings/useMeterSettingsForm";
import * as styles from "./MeterReadingsPage.styles";

const MeterReadingsPage = () => {
  const { t } = useTranslation(["common", "meterReadings"]);
  const { providers, selectedProviderId, selectedProvider, setSelectedProviderId, error: providersError, loadingProviders } = useMeterProviders();
  const { history, reloadHistory } = useMeterHistory(selectedProviderId);
  const { form, actions: settingsActions } = useMeterSettingsForm(selectedProvider);
  const { capture, actions: captureActions } = useMeterCapture(selectedProvider, {
    settings: form.settings,
    onConfirmed: () => void reloadHistory(),
  });

  const switchProvider = (providerId: number) => {
    setSelectedProviderId(providerId);
    captureActions.retake();
    captureActions.clearSuccess();
  };

  const tabItems = buildMeterProviderTabItems(providers);

  return (
    <div className={shared.page}>
      <PageHeader icon={Camera} title={t("meterReadings:pageTitle")} description={t("meterReadings:pageDescription")} />
      {providersError && <p className={shared.errorText}>{providersError}</p>}

      {!loadingProviders && !providers.length && (
        <div className={styles.section}>
          <NotEnabledCard />
        </div>
      )}

      {(loadingProviders || providers.length > 0) && (
        <>
          <ProviderTabs items={tabItems} selectedId={selectedProviderId} onSelect={switchProvider} />

          {selectedProvider && (
            <div className={styles.section}>
              <CaptureCard provider={selectedProvider} capture={capture} actions={captureActions} settingsIncomplete={form.settingsIncomplete} />
              <HistoryCard history={history} />
              <SettingsCard form={form} actions={settingsActions} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MeterReadingsPage;
